/**
 * Phase J.8-F11 (2026-08-03) — Edit Rental Pricing modal (super_admin).
 *
 * Wraps the pricing-correction endpoint with a UI that:
 *   - Shows the current rate + period.
 *   - Lets the operator switch billing_period + set the matching rate.
 *   - Enforces the J.8-F8 cross-field invariant client-side (the
 *     server enforces it too — this is UX polish).
 *   - Requires a correction reason (>=5 chars).
 *   - Warns if there are existing invoices referencing the current rate
 *     (they won't be auto-updated).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AlertTriangle } from "lucide-react";

export default function EditRentalPricingModal({ open, onClose, rental, onSaved }) {
    const [billingPeriod, setBillingPeriod] = useState(rental?.billing_period || "monthly");
    const [monthlyRate, setMonthlyRate] = useState(
        rental?.monthly_rate != null ? String(rental.monthly_rate) : "",
    );
    const [dailyRate, setDailyRate] = useState(
        rental?.daily_rate != null ? String(rental.daily_rate) : "",
    );
    const [reason, setReason] = useState("");
    const [ack, setAck] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [invoiceCount, setInvoiceCount] = useState(null);

    useEffect(() => {
        if (!open || !rental?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(
                    `/admin/rentals/${rental.id}/invoices`,
                );
                if (!cancelled) {
                    const rows = data?.invoices || data?.items || (Array.isArray(data) ? data : []);
                    setInvoiceCount(rows.length);
                }
            } catch {
                if (!cancelled) setInvoiceCount(0);
            }
        })();
        return () => { cancelled = true; };
    }, [open, rental?.id]);

    const invariantOK = useMemo(() => {
        const m = Number(monthlyRate || 0);
        const d = Number(dailyRate || 0);
        if (billingPeriod === "monthly") return m > 0 && d === 0;
        if (billingPeriod === "daily") return d > 0 && m === 0;
        return false;
    }, [billingPeriod, monthlyRate, dailyRate]);

    const changedFields = useMemo(() => {
        const changes = [];
        if (billingPeriod !== rental?.billing_period) {
            changes.push(`billing_period: ${rental?.billing_period} → ${billingPeriod}`);
        }
        const newM = Number(monthlyRate || 0);
        if (newM !== Number(rental?.monthly_rate || 0)) {
            changes.push(`monthly_rate: ${rental?.monthly_rate ?? "—"} → ${newM}`);
        }
        const newD = Number(dailyRate || 0);
        if (newD !== Number(rental?.daily_rate || 0)) {
            changes.push(`daily_rate: ${rental?.daily_rate ?? "—"} → ${newD}`);
        }
        return changes;
    }, [billingPeriod, monthlyRate, dailyRate, rental]);

    const canSubmit = invariantOK
        && reason.trim().length >= 5
        && ack
        && changedFields.length > 0
        && !submitting;

    const submit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                billing_period: billingPeriod,
                monthly_rate: Number(monthlyRate || 0),
                daily_rate: Number(dailyRate || 0),
                correction_reason: reason.trim(),
            };
            const { data } = await api.patch(
                `/admin/rentals/${rental.id}/pricing`, payload,
            );
            toast.success("Pricing updated. Audit trail recorded.");
            if (onSaved) onSaved(data);
            if (onClose) onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
            <DialogContent
                className="max-w-lg"
                data-testid="edit-rental-pricing-modal">
                <DialogHeader>
                    <DialogTitle>Edit rental pricing</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="rounded-md border bg-slate-50 p-3 text-xs text-slate-700">
                        <div className="font-semibold text-slate-900">Current</div>
                        <div className="tabular-nums">
                            {rental?.billing_period || "—"} ·{" "}
                            {rental?.billing_period === "daily"
                                ? `$${rental?.daily_rate ?? "—"}/day`
                                : `$${rental?.monthly_rate ?? "—"}/mo`}
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="edit-billing-period">Billing period</Label>
                        <Select value={billingPeriod} onValueChange={setBillingPeriod}>
                            <SelectTrigger
                                id="edit-billing-period"
                                data-testid="edit-pricing-period-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="monthly">Monthly</SelectItem>
                                <SelectItem value="daily">Daily</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {billingPeriod === "monthly" && (
                        <div>
                            <Label htmlFor="edit-monthly-rate">Monthly rate ($)</Label>
                            <Input
                                id="edit-monthly-rate"
                                data-testid="edit-pricing-monthly-input"
                                type="number" min="0" step="0.01"
                                value={monthlyRate}
                                onChange={(e) => {
                                    setMonthlyRate(e.target.value);
                                    setDailyRate("0");
                                }}
                            />
                        </div>
                    )}
                    {billingPeriod === "daily" && (
                        <div>
                            <Label htmlFor="edit-daily-rate">Daily rate ($)</Label>
                            <Input
                                id="edit-daily-rate"
                                data-testid="edit-pricing-daily-input"
                                type="number" min="0" step="0.01"
                                value={dailyRate}
                                onChange={(e) => {
                                    setDailyRate(e.target.value);
                                    setMonthlyRate("0");
                                }}
                            />
                        </div>
                    )}

                    {changedFields.length > 0 && (
                        <div
                            className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900"
                            data-testid="edit-pricing-diff-preview">
                            <div className="font-semibold uppercase tracking-wider mb-1">
                                Preview
                            </div>
                            <ul className="list-disc list-inside space-y-1 font-mono">
                                {changedFields.map((c, i) => (
                                    <li key={i}>{c}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div>
                        <Label htmlFor="edit-pricing-reason">
                            Correction reason (required, min 5 chars)
                        </Label>
                        <Textarea
                            id="edit-pricing-reason"
                            data-testid="edit-pricing-reason-input"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Imported with wrong billing_period — corrected to monthly per contract"
                            rows={2}
                        />
                    </div>

                    {invoiceCount > 0 && (
                        <div
                            className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-900 flex items-start gap-2"
                            data-testid="edit-pricing-invoice-warning">
                            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                            <span>
                                This rental has <strong>{invoiceCount}</strong>{" "}
                                existing invoice{invoiceCount === 1 ? "" : "s"}.
                                They will <strong>NOT</strong> be updated;
                                only new invoices will reflect the new rate.
                            </span>
                        </div>
                    )}

                    <label
                        className="flex items-start gap-2 text-xs text-slate-700"
                        data-testid="edit-pricing-ack-label">
                        <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={ack}
                            onChange={(e) => setAck(e.target.checked)}
                            data-testid="edit-pricing-ack-checkbox"
                        />
                        <span>
                            I understand existing invoices will NOT be updated;
                            only new invoices reflect the change.
                        </span>
                    </label>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={submitting}
                        data-testid="edit-pricing-cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={!canSubmit}
                        data-testid="edit-pricing-submit-btn">
                        {submitting ? "Saving…" : "Save pricing correction"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
